import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';
import { faSort, faSortDown, faSortUp, faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  Col,
  Panel,
  Row,
  Table,
  NavLinkButton,
  FormGroup,
  ControlLabel,
  Button,
  Alert,
} from '@shared/components/bootstrap';
import { Pagination } from '@shared/components/pagination';
import { useDebounce } from '@shared/hooks';
import { useNumericURLSearchParam } from '@shared/hooks/use_numeric_url_search_param';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';
import { useURLSearchParams } from '@shared/hooks/use_url_search_params';

import { InputFormGroup, SelectFormGroup, NumberFormControl } from '@admin/components/fields';
import { Label } from '@admin/components/helpers/label';
import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { COLLECTIONS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  usePaginatedCollectionsQuery,
  useDelinquencyStatesQuery,
  useCollectionsWarehousesQuery,
  AccountSortInput,
  OrderingDirectionEnum,
} from '@admin/schema';

const DEFAULT_PAGE = 1;

const DELINQUENCY_STATUS_TO_STYLE: Record<string, 'danger' | 'warning' | 'success' | 'info' | 'secondary' | 'dark'> = {
  'Grace Period': 'success',
  'Early delinquency': 'warning',
  Delinquency: 'warning',
  'Default (1st Notice)': 'danger',
  'Auction Sale (2nd Notice)': 'danger',
  'Auction Execution': 'danger',
  'Dispose/ Reconcile': 'dark',
};

const InputGroupList = styled.div`
  display: flex;
  margin: -4px;
`;

const InputGroupItem = styled.div`
  padding: 4px;
  flex-grow: 1;
  flex-shrink: 1;
`;

const InputGroupSeparator = styled.div`
  padding: 4px;
  flex-grow: 0;
  flex-shrink: 0;
  line-height: 34px;
`;

const QueryFormGroup: React.FC<{
  query?: string;
  onChange(query?: string): void;
}> = ({ query, onChange }) => (
  <InputFormGroup
    label="Search:"
    type="text"
    value={query ?? ''}
    onChange={(event) => onChange(event.target.value || undefined)}
    placeholder="Search by name, email, or phone"
  />
);

const DelinquencyStatusFormGroup: React.FC<{
  delinquencyStatus?: string;
  onChange(delinquencyStatus?: string): void;
  states: Array<{ id: string; name: string; minDaysOverdue: number }>;
}> = ({ delinquencyStatus, onChange, states }) => (
  <SelectFormGroup
    label="Delinquency Status:"
    onChange={(event) => onChange(event.target.value || undefined)}
    value={delinquencyStatus ?? ''}
  >
    <option value=""> - All - </option>
    {states.map((state) => (
      <option key={state.id} value={state.id}>
        {state.name}
      </option>
    ))}
  </SelectFormGroup>
);

const PrimaryWarehouseFormGroup: React.FC<{
  primaryWarehouse?: string;
  onChange(primaryWarehouse?: string): void;
  warehouses: Array<{ id: string; name: string }>;
}> = ({ primaryWarehouse, onChange, warehouses }) => (
  <SelectFormGroup
    label="Primary Warehouse:"
    onChange={(event) => onChange(event.target.value || undefined)}
    value={primaryWarehouse ?? ''}
  >
    <option value=""> - All - </option>
    {warehouses.map((warehouse) => (
      <option key={warehouse.id} value={warehouse.id}>
        {warehouse.name}
      </option>
    ))}
  </SelectFormGroup>
);

const DaysOverdueFormGroup: React.FC<{
  daysOverdueMin?: number;
  daysOverdueMax?: number;
  onChange(min?: number, max?: number): void;
}> = ({ daysOverdueMin, daysOverdueMax, onChange }) => (
  <FormGroup>
    <ControlLabel>Days Overdue:</ControlLabel>
    <InputGroupList>
      <InputGroupItem>
        <NumberFormControl
          value={daysOverdueMin ?? undefined}
          placeholder="Min"
          onChange={(min) => onChange(min, daysOverdueMax)}
        />
      </InputGroupItem>
      <InputGroupSeparator>-</InputGroupSeparator>
      <InputGroupItem>
        <NumberFormControl
          value={daysOverdueMax ?? undefined}
          placeholder="Max"
          onChange={(max) => onChange(daysOverdueMin, max)}
        />
      </InputGroupItem>
    </InputGroupList>
  </FormGroup>
);

const OverdueAmountFormGroup: React.FC<{
  overdueAmountMin?: number;
  overdueAmountMax?: number;
  onChange(min?: number, max?: number): void;
}> = ({ overdueAmountMin, overdueAmountMax, onChange }) => (
  <FormGroup>
    <ControlLabel>Overdue Amount (USD):</ControlLabel>
    <InputGroupList>
      <InputGroupItem>
        <NumberFormControl
          value={overdueAmountMin ?? undefined}
          placeholder="Min"
          onChange={(min) => onChange(min, overdueAmountMax)}
        />
      </InputGroupItem>
      <InputGroupSeparator>-</InputGroupSeparator>
      <InputGroupItem>
        <NumberFormControl
          value={overdueAmountMax ?? undefined}
          placeholder="Max"
          onChange={(max) => onChange(overdueAmountMin, max)}
        />
      </InputGroupItem>
    </InputGroupList>
  </FormGroup>
);

const CollectionsContent: React.FC = () => {
  useTitle('Collections');
  const [page, setPage] = useNumericURLSearchParam('page', DEFAULT_PAGE);
  const [query, setQuery] = useURLSearchParam('query');
  const [delinquencyStatus, setDelinquencyStatus] = useURLSearchParam('delinquencyStatus');
  const [daysOverdueMin, setDaysOverdueMin] = useNumericURLSearchParam('daysOverdueMin');
  const [daysOverdueMax, setDaysOverdueMax] = useNumericURLSearchParam('daysOverdueMax');
  const [overdueAmountMin, setOverdueAmountMin] = useNumericURLSearchParam('overdueAmountMin');
  const [overdueAmountMax, setOverdueAmountMax] = useNumericURLSearchParam('overdueAmountMax');
  const [primaryWarehouse, setPrimaryWarehouse] = useURLSearchParam('primaryWarehouse');
  const [sort, setSort] = useState<AccountSortInput | undefined>();
  const { data: delinquencyStatesData, error: delinquencyStatesError } = useDelinquencyStatesQuery({ client });
  const { data: warehousesData, error: warehousesError } = useCollectionsWarehousesQuery({ client });
  const { data, loading, error } = usePaginatedCollectionsQuery({
    client,
    variables: {
      page,
      query: useDebounce(query),
      delinquencyState: delinquencyStatus,
      daysOverdueMin: daysOverdueMin,
      daysOverdueMax: daysOverdueMax,
      // Convert dollars to cents (multiply by 100) since backend stores amounts in cents
      overdueAmountMin: overdueAmountMin !== undefined ? Math.round(overdueAmountMin * 100) : undefined,
      overdueAmountMax: overdueAmountMax !== undefined ? Math.round(overdueAmountMax * 100) : undefined,
      primaryWarehouse: primaryWarehouse,
      sort: sort,
    },
  });

  const onExport = () => {
    // Build query params from debounced filters to match what's displayed in the table
    const filters: Record<string, string> = {};
    if (query) filters.query = query;
    if (delinquencyStatus) filters.delinquency_state = delinquencyStatus;
    if (daysOverdueMin !== undefined) filters.days_overdue_min = String(daysOverdueMin);
    if (daysOverdueMax !== undefined) filters.days_overdue_max = String(daysOverdueMax);
    // Convert dollars to cents (multiply by 100) since backend stores amounts in cents
    if (overdueAmountMin !== undefined) filters.overdue_amount_min = String(Math.round(overdueAmountMin * 100));
    if (overdueAmountMax !== undefined) filters.overdue_amount_max = String(Math.round(overdueAmountMax * 100));
    if (primaryWarehouse) filters.primary_warehouse = primaryWarehouse;
    if (sort?.column) filters.sort_column = sort.column;
    if (sort?.direction) filters.sort_direction = sort.direction;

    // Build CSV export URL (same pattern as gratuities)
    const csvUrl = `/delinquency/exports/download.csv?${new URLSearchParams(filters).toString()}`;
    window.open(csvUrl, '_blank');
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const formatDelinquencyStatus = (status: string | null | undefined): string => {
    if (!status) return '-';
    return status;
  };

  const getDelinquencyStatusKind = (
    status: string | null | undefined,
  ): 'danger' | 'warning' | 'success' | 'info' | 'secondary' | 'dark' | undefined => {
    if (!status) return undefined;
    return DELINQUENCY_STATUS_TO_STYLE[status] || 'secondary';
  };

  const updateSort = (column: string) => {
    if (sort?.column !== column || sort?.direction === OrderingDirectionEnum.Ascending) {
      setSort({ column, direction: OrderingDirectionEnum.Descending });
    } else {
      setSort({ column, direction: OrderingDirectionEnum.Ascending });
    }
  };

  const SortIcon: React.FC<{
    column: string;
  }> = ({ column }) => {
    if (!sort || sort.column !== column) {
      return <FontAwesomeIcon icon={faSort} />;
    }
    return <FontAwesomeIcon icon={sort.direction === OrderingDirectionEnum.Ascending ? faSortUp : faSortDown} />;
  };

  return (
    <>
      <Panel>
        <Panel.Body>
          {delinquencyStatesError && (
            <Alert style="danger">Failed to load delinquency states. Please refresh the page.</Alert>
          )}
          {warehousesError && <Alert style="danger">Failed to load warehouses. Please refresh the page.</Alert>}
          <Row>
            <Col sm={12}>
              <QueryFormGroup query={query} onChange={setQuery} />
            </Col>
          </Row>
          <Row>
            <Col sm={3}>
              <DelinquencyStatusFormGroup
                delinquencyStatus={delinquencyStatus}
                onChange={setDelinquencyStatus}
                states={delinquencyStatesData?.delinquencyStates || []}
              />
            </Col>
            <Col sm={3}>
              <PrimaryWarehouseFormGroup
                primaryWarehouse={primaryWarehouse}
                onChange={setPrimaryWarehouse}
                warehouses={warehousesData?.primaryWarehousesWithSuspendedAccounts || []}
              />
            </Col>
            <Col sm={3}>
              <DaysOverdueFormGroup
                daysOverdueMin={daysOverdueMin}
                daysOverdueMax={daysOverdueMax}
                onChange={(min, max) => {
                  setDaysOverdueMin(min);
                  setDaysOverdueMax(max);
                }}
              />
            </Col>
            <Col sm={3}>
              <OverdueAmountFormGroup
                overdueAmountMin={overdueAmountMin}
                overdueAmountMax={overdueAmountMax}
                onChange={(min, max) => {
                  setOverdueAmountMin(min);
                  setOverdueAmountMax(max);
                }}
              />
            </Col>
          </Row>
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Body>
          {error && <Alert style="danger">Failed to load collections data. Please refresh the page.</Alert>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h4 style={{ margin: 0 }}>Collections</h4>
            <Button kind="primary" onClick={onExport}>
              <FontAwesomeIcon icon={faDownload} /> Export CSV
            </Button>
          </div>
          <Table responsive striped>
            <thead>
              <tr>
                <th className="col-sm-2 text-left" style={{ cursor: 'pointer' }} onClick={() => updateSort('number')}>
                  Account # <SortIcon column="number" />
                </th>
                <th
                  className="col-sm-2 text-left"
                  style={{ cursor: 'pointer' }}
                  onClick={() => updateSort('primary_warehouse')}
                >
                  Primary Warehouse <SortIcon column="primary_warehouse" />
                </th>
                <th
                  className="col-sm-2 text-left"
                  style={{ cursor: 'pointer' }}
                  onClick={() => updateSort('customer_name')}
                >
                  Name <SortIcon column="customer_name" />
                </th>
                <th
                  className="col-sm-2 text-left"
                  style={{ cursor: 'pointer' }}
                  onClick={() => updateSort('delinquency_status')}
                >
                  Delinquency Status <SortIcon column="delinquency_status" />
                </th>
                <th
                  className="col-sm-2 text-left"
                  style={{ cursor: 'pointer' }}
                  onClick={() => updateSort('days_overdue')}
                >
                  Days Overdue <SortIcon column="days_overdue" />
                </th>
                <th
                  className="col-sm-2 text-left"
                  style={{ cursor: 'pointer' }}
                  onClick={() => updateSort('overdue_amount')}
                >
                  Overdue Amount (USD) <SortIcon column="overdue_amount" />
                </th>
                <th className="col-zero" />
              </tr>
            </thead>
            <tbody>
              {data?.paginated.results.map((account) => {
                return (
                  <tr key={account.id}>
                    <td className="col-sm-2 text-left">{account.number}</td>
                    <td className="col-sm-2 text-left">{account.primaryWarehouse?.name ?? '-'}</td>
                    <td className="col-sm-2 text-left">{account.customer.name}</td>
                    <td className="col-sm-2 text-left">
                      {account.delinquencyStatus ? (
                        <Label kind={getDelinquencyStatusKind(account.delinquencyStatus)}>
                          {formatDelinquencyStatus(account.delinquencyStatus)}
                        </Label>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="col-sm-2 text-left">{account.daysOverdue ?? '-'}</td>
                    <td className="col-sm-2 text-left">{formatCurrency(account.overdueAmount)}</td>
                    <td className="col-zero">
                      <NavLinkButton kind="primary" to={`/accounts/${account.id}`}>
                        View
                      </NavLinkButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {loading && <Spinner />}
          {data && <Pagination pagination={data.paginated.pagination} onPage={setPage} />}
        </Panel.Body>
      </Panel>
    </>
  );
};

export const Collections: React.FC = () => (
  <Permission resource={COLLECTIONS.resource} action={COLLECTIONS.actions.read}>
    <CollectionsContent />
  </Permission>
);
